import { useMyUser } from '@consigli/hooks';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { CenteredLayout } from '@/layouts/centered-content';
import { FluidLayout } from '@/layouts/fluid-content';
import { Nav } from '@/layouts/nav';
import { useDemoMode } from '@/util/demo-mode';
import { formatDisplayName } from '@/util/format-name';

import { UserProfileInviteList } from './user-profile-invite-list';
import { UserProfileProjectAccessList } from './user-profile-project-access-list';

export const UserProfilePage: FC = () => {
  const { user } = useMyUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { demoModeEnabled, toggleDemoMode } = useDemoMode();

  return (
    <>
      <Breadcrumb label={t('navbar.breadcrumbs.myprofile')} to={`/profile`} />
      <FluidLayout>
        <CenteredLayout>
          <Nav handleClick={() => navigate('/projects')} isProfilePage={true} />
          <div className="flex w-full max-w-[600px] justify-center align-center p-12 mx-auto">
            <div className="flex flex-col w-full">
              <div className="flex items-center justify-between">
                <h1 className="flex text-3xl font-medium">
                  {formatDisplayName(user.firstName, user.lastName)}
                </h1>
                {user.isSuperuser && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-day-neutral-subtle">Demo mode</span>
                    <button
                      id="demo-mode-toggle"
                      aria-label="Toggle demo mode"
                      onClick={toggleDemoMode}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                        demoModeEnabled ? 'bg-day-primary' : 'bg-day-neutral-subtle/40'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 shadow ${
                          demoModeEnabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex justify-between mt-6">
                <div className="flex flex-col">
                  <p className="text-sm text-day-neutral-subtle">{t('profile.email')}</p>
                  <b>{user.email}</b>
                </div>
                <div className="flex flex-col items-end">
                  <p className="text-sm text-day-neutral-subtle">{t('profile.role')}</p>
                  <b>
                    {user.isSuperuser
                      ? 'SuperUser 🦸‍♂️'
                      : user.isOrgAdmin
                        ? t('profile.admin')
                        : t('profile.user')}
                  </b>
                </div>
              </div>
              <div className="flex justify-between mt-6">
                <div className="flex flex-col">
                  <p className="text-sm text-day-neutral-subtle">{t('profile.organization')}</p>
                  <b>{user.organizationName || '-'}</b>
                </div>
              </div>
              <div className="flex justify-between mt-6">
                <div className="flex flex-col">
                  <p className="text-sm text-day-neutral-subtle">{t('profile.country')}</p>
                  <b>{user.ssoCountry || '-'}</b>
                </div>
                <div className="flex flex-col items-end">
                  <p className="text-sm text-day-neutral-subtle">{t('profile.business-line')}</p>
                  <b>{user.ssoBusinessLine || '-'}</b>
                </div>
              </div>
              <div className="border-b border-dashed border-day-secondary my-6"></div>
              {!user.isSuperuser && <UserProfileInviteList />}
              <UserProfileProjectAccessList />
            </div>
          </div>
        </CenteredLayout>
      </FluidLayout>
    </>
  );
};
